/**
 * What a hosted agent can do on a call, and the systems it does it with.
 *
 * A TOOL IS A ROW, AND ITS SWITCH IS ON THE ROW. Turning one off is the commonest edit - a
 * booking system down for the afternoon - and it should not mean opening the editor. The
 * switch is a draft like everything else on the page and goes with the one Save; pressing
 * it never opens the tool, which a row click does.
 *
 * THE EDITOR IS IN THE URL (`&tool=<id>` or `&tool=new`), with `replace`, for the reason a
 * tab is: a tool is not a destination Back should walk through, but "look at the booking
 * tool" is a link somebody can send.
 *
 * CONNECTIONS SIT UNDER THE TOOLS because they are what the tools are built on, and shared:
 * a Shopify connection is written once and used by three tools. Adding or changing one still
 * opens `ConnectionDialog`, which is not the prototype's inline row: that row had a name, a
 * kind and a secret, and a connection is also a base address, a header name or a username.
 * A secret saves the moment its dialog does, because a key can't be kept in a draft.
 */

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { AgentPage, Connection } from "../../../api";
import { ConnectionDialog } from "../../agent/ConnectionDialog";
import { ToolEditor } from "../../agent/tool-editor/ToolEditor";
import { Button, CardHead, CountTag, SettingCard, Switch, cx } from "../../../ui";
import { useDraft } from "../draft";

/** The Worker's caps. */
const MAX_TOOLS = 25;
const MAX_CONNECTIONS = 10;

const AVAILABILITY: Record<string, string> = { always: "Always", open: "Open hours", closed: "After hours" };

const AUTH: Record<Connection["auth"], string> = {
  none: "No sign-in",
  bearer: "Bearer token",
  header: "API key header",
  basic: "User name and password",
};

export function ToolsPane({ agentId, page, onChanged }: { agentId: string; page: AgentPage; onChanged: () => void }) {
  const { state, set } = useDraft();
  const [params, setParams] = useSearchParams();
  const [connection, setConnection] = useState<Connection | null | undefined>(undefined);
  const tools = page.tools ?? [];
  const connections = page.connections ?? [];
  const enabled = state.draft.tools;

  const open = params.get("tool");
  const editing = open === "new" ? null : (tools.find((tool) => tool.id === open) ?? undefined);

  const openTool = (id: string | null) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (id) next.set("tool", id);
        else next.delete("tool");
        return next;
      },
      { replace: true },
    );

  return (
    <>
      <SettingCard flush>
        <CardHead
          title="Tools"
          count={<CountTag n={tools.length} max={MAX_TOOLS} />}
          aside={
            <Button type="button" variant="primary" disabled={tools.length >= MAX_TOOLS} onClick={() => openTool("new")}>
              New tool
            </Button>
          }
        />
        {tools.length === 0 && (
          <p className="px-5 py-6 text-sm text-ink-faint">No tools yet. Your agent can still talk and answer from its instructions.</p>
        )}
        {tools.map((tool, index) => {
          const on = enabled[tool.id] ?? tool.enabled;
          return (
            <div
              key={tool.id}
              className={cx(
                "grid cursor-pointer grid-cols-[minmax(0,1fr)_auto_auto_56px] items-center gap-4 px-5 py-3.5 hover:bg-ground",
                index > 0 && "border-t border-line-soft",
                "max-[720px]:grid-cols-[minmax(0,1fr)_56px]",
              )}
              onClick={() => openTool(tool.id)}
            >
              <button type="button" className="grid min-w-0 gap-0.5 text-left" onClick={(event) => (event.stopPropagation(), openTool(tool.id))}>
                <span className={cx("font-mono text-base font-medium", on ? "text-ink" : "text-ink-faint")}>{tool.name}</span>
                <span className="truncate text-xs text-ink-faint">{tool.description}</span>
              </button>
              <span className="rounded-full bg-line-soft px-2.5 py-0.5 text-2xs font-medium whitespace-nowrap text-ink-soft max-[720px]:hidden">
                {AVAILABILITY[tool.availability]}
              </span>
              <span className="rounded-xs border border-line px-2.5 py-0.5 font-mono text-2xs font-medium whitespace-nowrap text-ink-soft max-[720px]:hidden">
                {tool.kind === "save" ? "Save to call" : tool.kind === "code" ? "Runs code" : `${tool.http?.method ?? "GET"} request`}
              </span>
              <span className="justify-self-end" onClick={(event) => event.stopPropagation()}>
                <Switch checked={on} label={`${tool.name} on`} onChange={(next) => set("tools", { ...enabled, [tool.id]: next })} />
              </span>
            </div>
          );
        })}
      </SettingCard>
      {!page.hoursKnown && tools.some((tool) => tool.availability !== "always") && (
        <p className="-mt-2 text-xs text-warm">
          Some tools only run in or out of opening hours. Set a timezone and hours, or they are offered all the time.
        </p>
      )}

      <SettingCard flush>
        <CardHead
          title="Connections"
          count={<CountTag n={connections.length} max={MAX_CONNECTIONS} />}
          note="Saved logins for other systems. A key can't be viewed again after saving."
          aside={
            <Button type="button" disabled={connections.length >= MAX_CONNECTIONS} onClick={() => setConnection(null)}>
              Add connection
            </Button>
          }
        />
        {connections.length === 0 && <p className="px-5 py-6 text-sm text-ink-faint">No connections yet.</p>}
        {connections.map((row, index) => (
          <button
            key={row.id}
            type="button"
            onClick={() => setConnection(row)}
            className={cx(
              "grid w-full grid-cols-[minmax(0,1fr)_170px_150px_120px] items-center gap-4 px-5 py-3 text-left hover:bg-ground",
              index > 0 && "border-t border-line-soft",
              "max-[720px]:grid-cols-[minmax(0,1fr)_auto]",
            )}
          >
            <span className="grid min-w-0">
              <span className="text-base font-medium text-ink">{row.name}</span>
              <span className="truncate font-mono text-2xs text-ink-faint">{row.baseUrl}</span>
            </span>
            <span className="text-xs text-ink-soft max-[720px]:hidden">{AUTH[row.auth]}</span>
            <span className="font-mono text-xs text-ink-faint max-[720px]:hidden">
              {row.auth === "none" ? "No key" : row.secretSet ? `••••${row.secretLast4 ?? ""}` : "Key missing"}
            </span>
            <span className="text-right text-xs text-ink-soft">
              {row.usedBy === 0 ? "Not used" : `Used by ${row.usedBy} ${row.usedBy === 1 ? "tool" : "tools"}`}
            </span>
          </button>
        ))}
      </SettingCard>

      {editing !== undefined && (
        <ToolEditor
          key={open ?? "new"}
          agentId={agentId}
          tool={editing}
          connections={connections}
          hoursKnown={page.hoursKnown}
          enabled={editing && state.draft.tools[editing.id] !== state.baseline.tools[editing.id] ? state.draft.tools[editing.id] : undefined}
          onClose={() => openTool(null)}
          onSaved={onChanged}
        />
      )}
      {connection !== undefined && (
        <ConnectionDialog agentId={agentId} connection={connection} onClose={() => setConnection(undefined)} onSaved={onChanged} />
      )}
    </>
  );
}
